import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Trans, useTranslation } from 'react-i18next';
import {
	Checkbox,
	FormControlLabel,
	Grid,
	Link,
	TextField,
	Typography,
	Divider,
	Autocomplete,
	Chip,
} from '@mui/material';
import * as Actions from 'app/store/actions';
import { getPrivacyPolicyUrl, isValidEmail, sanitizeFilename } from 'app/utils/helpers';
// @ts-ignore: missing type declaration file
import { useScreenshot } from 'use-react-screenshot';
import { useDispatch, useSelector } from 'app/modules/react-redux';
import { getProfile } from 'app/store/reducers';
import { type Workflow } from 'app/store/types';
import AlertDialog from 'app/components/AlertDialog';
import StyledDropzone from 'app/components/StyledDropzone';
import { useHistory } from 'react-router';

const EXCLUDE_FILE_TYPES = [
	'application/exe',
	'application/dos-exe',
	'application/msdos-windows',
	'application/vnd.microsoft.portable-executable',
	'application/x-exe',
	'application/x-msdos-program',
	'application/x-msdownload',
	'application/x-ms-dos-executable',
	'application/x-winexe',
	'vms/exe',
];
const MAX_FILE_SIZE = 1048576 * 25; // 25MB

const useStyles = makeStyles((theme) => ({
	boldText: {
		fontWeight: 600,
		fontSize: '21px',
	},
	normalText: {
		fontWeight: 400,
		color: '#666666',
		fontSize: '14px',
	},
	text: {
		'& .MuiFormHelperText-root.Mui-error': {
			// fontSize: 8,
			margin: 0,
		},
		'& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
			borderColor: '#f44336',
		},
		'& .MuiInputBase-root': {
			paddingTop: '8px',
			height: 'auto',
		},
	},
	checkbox: {
		padding: '0px',
		margin: '9px',
	},
	divider: {
		backgroundColor: '#707070',
	},
	items: {
		'& > .MuiGrid-item': {
			padding: '12px 0px',
		},
		'& > .MuiGrid-item:first-child': {
			padding: '0px 0px 12px',
		},
		'& > .MuiGrid-item:last-child': {
			padding: '12px 0px 0px',
		},
	},
}));

type FeedbackProps = {
	children?: React.ReactElement;
	workflowId?: Workflow['id'];
	workflowName?: Workflow['name'];
};

const FeedbackDialog = ({ children, workflowId, workflowName }: FeedbackProps) => {
	const { t } = useTranslation();
	const history = useHistory();
	const dispatch = useDispatch();
	const classes = useStyles();
	const profile = useSelector(getProfile);

	const userEmail = profile.email;

	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

	const [, takeScreenShot] = useScreenshot({
		type: 'image/jpeg',
		quality: 1.0,
	});

	const getScreenshotFile = async () => {
		const screenshot = await takeScreenShot(document.documentElement);
		const imageData = await fetch(screenshot);
		const blob = await imageData.blob();
		const screenshotFile = new File([blob], 'screenshot.jpg', {
			type: blob.type,
		});

		return screenshotFile;
	};

	const [toggleFeedbackOpen, setToggleFeedbackOpen] = useState<boolean>(false);
	const [shareText, setShareText] = useState<string>('');
	const [emails, setEmails] = useState([userEmail]);
	const [newEmailValue, setNewEmailValue] = useState('');
	const [submitted, setSubmitted] = useState<boolean>(false);

	const resetForm = () => {
		setShareText('');
		setEmails([]);
		setNewEmailValue('');
		setSelectedFiles([]);
	};

	const uniqueArray = <T,>(arr: T[]): T[] => [...new Set(arr)];

	const hasTopLevelDomain = (str: string) => {
		return str.split('.').length > 1 && str.split('.')[1].length > 1;
	};

	const handleOpen = async () => {
		setToggleFeedbackOpen(true);
		let workflowFile;

		// Note: need to take a screenshot before showing the feedback dialog
		const screenshotFile = await getScreenshotFile();

		if (workflowId) {
			const response = await Actions.getWorkflow(workflowId, profile?.awsRegion);
			if (response?.data) {
				workflowFile = new File([response.data], `${sanitizeFilename(workflowName!)}.wfxc`, {
					type: 'application/zip',
				});
			}
		}

		setSelectedFiles(workflowFile ? [workflowFile, screenshotFile] : [screenshotFile]);
	};

	const handleClose = () => {
		setToggleFeedbackOpen(false);
		resetForm();
	};

	const onSubmit = () => {
		dispatch(Actions.sendCustomerFeedback(shareText, emails, selectedFiles));
		setSubmitted(true);
		resetForm();
	};

	const emailAddressError = !(
		newEmailValue === '' ||
		((isValidEmail(newEmailValue.trim()) || newEmailValue.trim() === '') && hasTopLevelDomain(newEmailValue))
	);

	useEffect(() => {
		if (workflowId) {
			return;
		}
		const searchParams = new URLSearchParams(history.location.search);
		if (searchParams.has('customerFeedback')) {
			handleOpen();
			searchParams.delete('customerFeedback');
			history.replace({ search: searchParams.toString() });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workflowId, history, history.location.search]);

	return (
		<>
			{children
				? React.cloneElement(children, {
						onClick: (e: any) => {
							// e?.preventDefault?.();
							// console.log(children.props);
							if (children?.props?.onClick) children.props.onClick();
							handleOpen();
						},
					})
				: undefined}
			{submitted && (
				<AlertDialog
					open={!!setSubmitted}
					setOpen={(open) => setSubmitted(open)}
					onClose={() => setSubmitted(false)}
					title={t('Customer Feedback')}
					maxWidth="sm"
					content={
						<>
							<Typography className={classes.boldText} gutterBottom>
								{t('feedback:dialog:thank you')}
							</Typography>
							<Typography className={classes.normalText}>
								{t('feedback:dialog:thank you privacy')}
							</Typography>
						</>
					}
					action={() => {}}
					buttonsText={[t('Close')]}
					secondaryButton={false}
				/>
			)}
			<AlertDialog
				open={!!toggleFeedbackOpen}
				setOpen={(open) => setToggleFeedbackOpen(open)}
				onClose={handleClose}
				title={t('Customer Feedback')}
				maxWidth="sm"
				resource={
					<Grid className={`${classes.items}`} container direction="column">
						<Grid className="pt-0" item>
							<Typography className={`${classes.boldText} text-center`} gutterBottom>
								{t('feedback:dialog:description')}
							</Typography>
							<Typography className={classes.normalText}>
								<Trans
									i18nKey="feedback:dialog:privacy"
									t={t}
									components={[
										<Link
											key="privacy"
											href={getPrivacyPolicyUrl(profile?.awsRegion)}
											target="_blank"
											rel="noreferrer noopener"
										/>,
									]}
								/>
							</Typography>
						</Grid>
						<Grid item>
							<Divider className={classes.divider} variant="fullWidth" />
						</Grid>
						<Grid item container direction="column">
							<Typography className={classes.boldText}>{t('feedback:dialog:who')}</Typography>
							<Typography className={classes.normalText}>
								{t('feedback:dialog:who description')}
							</Typography>
							<FormControlLabel
								style={{ pointerEvents: 'none' }}
								control={
									<Checkbox
										className={classes.checkbox}
										checked={emails.includes(userEmail)}
										onChange={(_event, checked) =>
											checked
												? setEmails([userEmail, ...emails])
												: setEmails(emails.filter((email) => email !== userEmail))
										}
										style={{ pointerEvents: 'auto' }}
									/>
								}
								label={
									<Typography variant="caption">
										{t('feedback:dialog:who label', { email: userEmail })}
									</Typography>
								}
							/>

							<Autocomplete
								multiple
								freeSolo
								options={[]}
								value={emails}
								onChange={(_event, value) => {
									setEmails(value);
									if (value.length === 0) {
										setNewEmailValue('');
									}
								}}
								renderTags={(value: string[], getTagProps) =>
									value.map((option: string, index: number) => (
										<Chip {...getTagProps({ index })} key={option} label={option} className="m-2" />
									))
								}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="outlined"
										type="email"
										value={newEmailValue}
										onChange={(event) => {
											setNewEmailValue(event.currentTarget.value);
										}}
										onKeyDown={(event) => {
											if (event.key === 'Enter' && emailAddressError) {
												event.stopPropagation();
											}
										}}
										error={emailAddressError}
										helperText={emailAddressError ? t('Invalid email address') : undefined}
										onPaste={(event: React.ClipboardEvent<HTMLElement>) => {
											event.preventDefault();
											const clipboardEmails = event.clipboardData
												.getData('Text')
												.split(/[,;\s]+/)
												.filter((clipboardEmail) => isValidEmail(clipboardEmail));
											setEmails((prevState) => uniqueArray([...prevState, ...clipboardEmails]));
										}}
									/>
								)}
								onBlur={() => {
									if (isValidEmail(newEmailValue)) {
										setEmails((prevState) => uniqueArray([...prevState, newEmailValue]));
									}
									setNewEmailValue('');
								}}
								clearOnBlur
								disableClearable
							/>
						</Grid>
						<Grid item container direction="column">
							<Grid className="items-center" item container direction="row">
								<Typography className={`${classes.boldText} mr-12`}>
									{t('feedback:dialog:what')}
								</Typography>
								<Typography variant="body1">{`(${t('Required')})`}</Typography>
							</Grid>
							<TextField
								variant="outlined"
								multiline
								rows={3}
								value={shareText}
								onChange={(e) => {
									setShareText(e.target.value);
								}}
							/>
						</Grid>
						<Grid className="pb-0" item>
							<StyledDropzone
								maxSize={MAX_FILE_SIZE}
								excludeTypes={EXCLUDE_FILE_TYPES}
								files={{ selectedFiles, setSelectedFiles }}
								errorMessages={{
									duplicate: <Trans i18nKey="feedback:dialog:duplicate file error" />,
									size: <Trans i18nKey="feedback:dialog:max file size error" />,
									blocked: <Trans i18nKey="feedback:dialog:blocked file error" />,
								}}
								content={
									<>
										<Typography variant="h6" gutterBottom>
											{t('feedback:dialog:files')}
										</Typography>
										<Typography variant="subtitle1" gutterBottom>
											{t('feedback:dialog:files description')}
										</Typography>
										<Typography variant="caption">{t('File(s)')}:</Typography>
										{selectedFiles.length === 0 && (
											<Typography variant="caption">{t('No file(s) selected')}</Typography>
										)}
									</>
								}
							/>
						</Grid>
					</Grid>
				}
				disabledBtn={shareText.length === 0}
				action={onSubmit}
				buttonsText={[t('Send')]}
				secondaryButton={false}
			/>
		</>
	);
};

export default FeedbackDialog;
